import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useTheme } from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import { iepaProd } from "../../constants/global";

const ViewCustomer = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);
  const [customerId, setCustomerId] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [cities, setCities] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const { selectedRows, customers, isEditMode } = location.state || {};

  /* ---------------- LOAD CUSTOMER ---------------- */
  useEffect(() => {
    if (selectedRows?.length === 1) {
      const c = customers.find((x) => x.id === selectedRows[0]);
      setCustomer(c);
      setCustomerId(c.id);

      // Fetch cities for the customer's pincode automatically
      if (c?.defaultLocation?.address?.postalCode) {
        fetchCityState(c.defaultLocation.address.postalCode);
      }
    }
    setIsEditing(!!isEditMode);
  }, [selectedRows, customers, isEditMode]);

  /* ---------------- FETCH CITY ---------------- */
  const fetchCityState = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;
    const res = await fetch(
      `${iepaProd.iepaUserSrvAPI}/iepa/api/city/state/list/pincode/${pincode}`,
    );
    setCities(await res.json());
  };

  /* ---------------- FETCH LOCATIONS ---------------- */
  useEffect(() => {
    fetch(
      `${iepaProd.iepaInventorySrvAPI}/inventory/routes/active/destinations/org/1`,
    )
      .then((r) => r.json())
      .then((d) =>
        setLocationOptions(d.map((x) => ({ value: x.id, label: x.name }))),
      );

    fetch(`${iepaProd.iepaInventorySrvAPI}/inventory/routes/active/org/1`)
      .then((r) => r.json())
      .then((d) =>
        setRouteOptions(d.map((x) => ({ value: x.id, label: x.name }))),
      );
  }, []);

  /* ---------------- INITIAL VALUES ---------------- */
  const initialValues = {
    customerNumber: customer?.customerNumber || "",
    customerName: customer?.name || "",
    ownerName: customer?.ownerName || "",
    customerType: customer?.type || "",
    email: customer?.contact?.email || "",
    phone: customer?.contact?.phone || "",
    address: customer?.defaultLocation?.address?.address_1 || "",
    pincode: customer?.defaultLocation?.address?.postalCode || "",
    city: customer?.defaultLocation?.address?.city || "",
    state: customer?.defaultLocation?.address?.state || "",
    location: "",
    route: "",
    profile: "",
    taxCategory: "",
  };

  /* ---------------- SAVE HANDLER ---------------- */
  const handleSave = async (values) => {
    setIsLoading(true);
    setMessage(null);
    setError(null);

    try {
      if (activeTab === 0) {
        await fetch(`${iepaProd.iepaUserSrvAPI}/customer/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: customerId,
            customerNumber: values.customerNumber,
            name: values.customerName,
            ownerName: values.ownerName,
            type: values.customerType,
            orgId: 1,
            updatedBy: 1,
          }),
        });
      }

      if (activeTab === 1) {
        await fetch(
          `${iepaProd.iepaUserSrvAPI}/customer/${customerId}/save/contact`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: values.email,
              phone: values.phone,
              orgId: 1,
              updatedBy: 1,
            }),
          },
        );
      }

      if (activeTab === 2) {
        await fetch(
          `${iepaProd.iepaUserSrvAPI}/customer/${customerId}/save/address`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address_1: values.address,
              city: values.city,
              state: values.state,
              postalCode: values.pincode,
              country: "India",
              orgId: 1,
              updatedBy: 1,
            }),
          },
        );
      }

      if (activeTab === 3) {
        await fetch(
          `${iepaProd.iepaUserSrvAPI}/customer/${customerId}/location/assign/route_destination/org/1`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              routeDestinationId: values.location,
              routeId: values.route,
              profile: values.profile,
              taxCategory: values.taxCategory,
              orgId: 1,
              updatedBy: 1,
            }),
          },
        );
      }

      setMessage("Saved successfully");
    } catch {
      setError("Save failed");
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- VALIDATION ---------------- */
  const getSchema = () => {
    if (activeTab === 0) return overviewSchema;
    if (activeTab === 1) return contactSchema;
    if (activeTab === 2) return addressSchema;
    return yup.object();
  };

  return (
    <Box m={2}>
      <Header title="View Customer" subtitle="View / Edit Customer" />

      <Formik
        initialValues={initialValues}
        validationSchema={getSchema()}
        onSubmit={handleSave}
        enableReinitialize
      >
        {(formik) => (
          <>
            {/* ACTION BAR */}
            <Box display="flex" gap={2} mb={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={formik.submitForm}
                disabled={!isEditing || isLoading}
                sx={{
                  backgroundColor: "#1976d2", // Blue background
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#115293", // Darker blue on hover
                  },
                }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/CustomerList")}
                sx={{
                  color: "#000", // Black text
                  borderColor: "#000", // Black border
                  "&:hover": {
                    backgroundColor: "#f5f5f5", // Light gray on hover
                    borderColor: "#000",
                  },
                }}
              >
                Cancel
              </Button>
            </Box>

            {message && <Typography color="green">{message}</Typography>}
            {error && <Typography color="red">{error}</Typography>}

            <Card>
              <CardContent>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                  <Tab label="Overview" />
                  <Tab label="Contact" />
                  <Tab label="Address" />
                  <Tab label="Assignment" />
                </Tabs>

                {/* OVERVIEW */}
                {activeTab === 0 && (
                  <Box mt={2} display="grid" gap={2}>
                    <TextField
                      name="customerNumber"
                      label="Customer Number"
                      value={formik.values.customerNumber}
                      onChange={formik.handleChange}
                      InputProps={{ readOnly: !isEditing }}
                    />
                    <TextField
                      name="customerName"
                      label="Customer Name"
                      value={formik.values.customerName}
                      onChange={formik.handleChange}
                    />
                    <TextField
                      name="ownerName"
                      label="Owner Name"
                      value={formik.values.ownerName}
                      onChange={formik.handleChange}
                    />
                    <TextField
                      name="customerType"
                      label="Customer Type"
                      value={formik.values.customerType}
                      onChange={formik.handleChange}
                    />
                  </Box>
                )}

                {/* CONTACT */}
                {activeTab === 1 && (
                  <Box mt={2} display="grid" gap={2}>
                    <TextField
                      name="email"
                      label="Email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                    />
                    <TextField
                      name="phone"
                      label="Phone"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                    />
                  </Box>
                )}

                {/* ADDRESS */}
                {activeTab === 2 && (
                  <Box mt={2} display="grid" gap={2}>
                    <TextField
                      name="address"
                      label="Address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                    />
                    <TextField
                      name="pincode"
                      label="Pincode"
                      value={formik.values.pincode}
                      onChange={(e) => {
                        formik.handleChange(e);
                        fetchCityState(e.target.value);
                      }}
                    />
                    <TextField
                      select
                      name="city"
                      label="City"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                    >
                      {cities.map((c, i) => (
                        <MenuItem key={i} value={c.city}>
                          {c.city}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      name="state"
                      label="State"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                    >
                      {cities.map((c, i) => (
                        <MenuItem key={i} value={c.state}>
                          {c.state}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}

                {/* ASSIGNMENT */}
                {activeTab === 3 && (
                  <Box mt={2} display="grid" gap={2}>
                    <TextField
                      select
                      name="location"
                      label="Location"
                      value={formik.values.location}
                      onChange={formik.handleChange}
                    >
                      {locationOptions.map((l) => (
                        <MenuItem key={l.value} value={l.value}>
                          {l.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      name="city"
                      label="Route"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                    >
                      {cities.map((c, i) => (
                        <MenuItem key={i} value={c.city}>
                          {c.city}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      name="profile"
                      label="Profile"
                      value={formik.values.profile}
                      onChange={formik.handleChange}
                    />

                    <TextField
                      name="taxCategory"
                      label="Tax Category"
                      value={formik.values.taxCategory}
                      onChange={formik.handleChange}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Formik>
    </Box>
  );
};

/* ---------------- VALIDATION ---------------- */
const overviewSchema = yup.object({
  customerNumber: yup.string().required("Customer Number is required"),
  customerName: yup.string().required("Customer Name is required"),
  ownerName: yup.string().required("Owner Name is required"),
});

const contactSchema = yup.object({
  email: yup.string().email().required(),
  phone: yup.string().required(),
});

const addressSchema = yup.object({
  address: yup.string().required(),
  pincode: yup.string().length(6).required(),
  city: yup.string().required(),
  state: yup.string().required(),
});

export default ViewCustomer;
